const DEFAULT_DEPLOYMENT_POLL_INTERVAL_MS = 5 * 60 * 1000;
const DEPLOYMENT_MISMATCH_RELOAD_ID_KEY = "deployment-mismatch-reload-id";
const DEPLOYMENT_MISMATCH_RELOAD_TIMESTAMP_KEY = "deployment-mismatch-reload-timestamp";
const DEPLOYMENT_MISMATCH_RELOAD_TTL_MS = 15 * 60 * 1000;

function getLocalStorage(): Pick<Storage, "getItem" | "setItem"> | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  return window.localStorage;
}

export function hasDeploymentChanged(clientBuildId: string | undefined, serverBuildId: string | undefined): boolean {
  if (!clientBuildId || !serverBuildId) {
    return false;
  }

  return clientBuildId !== serverBuildId;
}

export function shouldReloadForMismatchDeploymentId(
  deploymentId: string,
  storage: Pick<Storage, "getItem" | "setItem"> | undefined = getLocalStorage(),
  dedupeTtlMs = DEPLOYMENT_MISMATCH_RELOAD_TTL_MS,
): boolean {
  if (!deploymentId || !storage) {
    return true;
  }

  try {
    const now = Date.now();
    const lastReloadedDeploymentId = storage.getItem(DEPLOYMENT_MISMATCH_RELOAD_ID_KEY);
    const lastReloadedTimestamp = Number(storage.getItem(DEPLOYMENT_MISMATCH_RELOAD_TIMESTAMP_KEY) ?? 0);
    const shouldDedupe =
      lastReloadedDeploymentId === deploymentId &&
      Number.isFinite(lastReloadedTimestamp) &&
      now - lastReloadedTimestamp < dedupeTtlMs;

    if (shouldDedupe) {
      return false;
    }

    storage.setItem(DEPLOYMENT_MISMATCH_RELOAD_ID_KEY, deploymentId);
    storage.setItem(DEPLOYMENT_MISMATCH_RELOAD_TIMESTAMP_KEY, String(now));
  } catch {
    return true;
  }

  return true;
}

export interface StartDeploymentPollingParams {
  checkForNewDeployment: () => Promise<boolean>;
  onNewDeployment: () => void;
  intervalMs?: number;
}

export function startDeploymentPolling({
  checkForNewDeployment,
  onNewDeployment,
  intervalMs = DEFAULT_DEPLOYMENT_POLL_INTERVAL_MS,
}: StartDeploymentPollingParams): () => void {
  const pollForNewDeployment = async (): Promise<void> => {
    if (await checkForNewDeployment()) {
      onNewDeployment();
    }
  };

  void pollForNewDeployment();
  const intervalId = setInterval(() => {
    void pollForNewDeployment();
  }, intervalMs);

  return () => clearInterval(intervalId);
}
